import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { CarmenUser } from "@carmen/auth-client";
import { signOut } from "@/lib/auth";
import { IconMenu } from "./Icons";

export function AdminPageHeader({
  title,
  subtitle,
  controls,
  belowTitle,
  user,
  accessRole,
  onOpenMobileMenu,
  showLogo,
}: {
  title: string;
  subtitle?: React.ReactNode;
  controls?: React.ReactNode;
  belowTitle?: React.ReactNode;
  user: CarmenUser | null;
  accessRole: string;
  onOpenMobileMenu?: () => void;
  showLogo?: boolean;
}) {
  return (
    <header className="relative z-30 border-b border-[rgba(10,52,96,0.10)] bg-[rgba(255,255,255,0.92)] px-6 py-5 backdrop-blur-xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {onOpenMobileMenu ? (
            <button
              type="button"
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              className="md:hidden mt-1 text-[rgba(10,52,96,0.62)] hover:text-[var(--cs-navy)]"
            >
              <IconMenu />
            </button>
          ) : null}
          {showLogo ? (
            <Link to="/" aria-label="Carmen home" className="mt-0.5 shrink-0">
              <img src="/carmen-logo.png" alt="Carmen" className="h-8 w-auto" />
            </Link>
          ) : null}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--cs-navy)]">
              {title}
            </h1>
            {subtitle ? (
              <div className="mt-1 text-sm text-[rgba(10,52,96,0.62)]">{subtitle}</div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {controls}
          <UserMenu user={user} accessRole={accessRole} />
        </div>
      </div>
      {belowTitle ? <div className="mt-3">{belowTitle}</div> : null}
    </header>
  );
}

function UserMenu({ user, accessRole }: { user: CarmenUser | null; accessRole: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  if (!user) return null;
  const meta = user.user_metadata as Record<string, unknown> | null;
  const fullName = typeof meta?.full_name === "string" && meta.full_name ? meta.full_name : null;
  const email = user.email ?? "";
  const label = fullName ?? email ?? "Account";
  const initial = (label || "?").charAt(0).toUpperCase();
  const roleLabel = accessRole.replace(/_/g, " ");

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-[rgba(10,52,96,0.10)] bg-white py-1 pl-1 pr-3 text-left transition-colors hover:bg-[var(--cs-ivory)]"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(225,29,72,0.10)] text-xs font-semibold text-[var(--cs-rose)]">
          {initial}
        </span>
        <span className="hidden min-w-0 flex-col sm:flex">
          <span className="truncate text-[12px] font-medium text-[var(--cs-navy)]">
            {label}
          </span>
          <span className="cs-caption mt-0.5 normal-case tracking-normal">
            {roleLabel}
          </span>
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white py-1 shadow-[0_24px_48px_rgba(10,52,96,0.14)]"
        >
          <div className="border-b border-[rgba(10,52,96,0.10)] px-3 py-2">
            <p className="truncate text-xs font-medium text-[var(--cs-navy)]">{label}</p>
            <p className="truncate text-[10px] text-[var(--cs-ink-muted)]">{email}</p>
            <p className="cs-caption mt-1 normal-case tracking-normal text-[var(--cs-rose)]">
              {roleLabel}
            </p>
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
          >
            Profiel
          </Link>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="block w-full px-3 py-2 text-left text-xs text-[var(--cs-rose)] hover:bg-[var(--cs-ivory)]"
          >
            Uitloggen
          </button>
        </div>
      ) : null}
    </div>
  );
}
